import { useState } from "react";
import { Box, Column, Row, Text } from "@hightouch/htui";
import {
  ResponsiveContainer,
  ComposedChart,
  Line,
  XAxis,
  YAxis,
  Tooltip as RechartsTooltip,
  Legend as RechartsLegend,
} from "recharts";
import type { TooltipProps } from "recharts";

import { CHART_COLORS } from "src/ui/colors";
import {
  getXAxisStyles,
  getYAxisStyles,
  getTooltipCursorStyles,
} from "src/components/charts/chart-utils";
import { renderCartesianGrid } from "src/components/charts/chart-grid";
import {
  ChartTooltipWrapper,
  ChartTooltipColor,
} from "src/components/charts/chart-tooltip";
import { Legend as PillLegend } from "src/components/analytics/shared/legend";

import type {
  LineChartResult,
  ChartConfig,
  LabelResolver,
  SendInsightDimension,
  DisplayFormat,
} from "./types";
import { getDisplayFormat } from "./types";
import { getDenominatorSource, getNumeratorSource } from "./metric-source";

const SERIES_COLORS = [
  CHART_COLORS.blue.base,
  CHART_COLORS.green.base,
  CHART_COLORS.purple.base,
  CHART_COLORS.pink.base,
  CHART_COLORS.orange.base,
  CHART_COLORS.yellow.base,
  CHART_COLORS.brown.base,
  CHART_COLORS.red.base,
];

function resolveSeriesLabel(
  segments: Record<string, string | number>,
  resolveLabel: LabelResolver,
): string {
  const entries = Object.entries(segments);
  if (entries.length === 0) return "All";

  return entries
    .map(([dim, val]) => resolveLabel(dim as SendInsightDimension, val).name)
    .join(" / ");
}

function deduplicateNames(names: string[]): string[] {
  const counts = new Map<string, number>();
  for (const name of names) {
    counts.set(name, (counts.get(name) ?? 0) + 1);
  }
  const seen = new Map<string, number>();
  return names.map((name) => {
    if (counts.get(name)! <= 1) return name;
    const idx = (seen.get(name) ?? 0) + 1;
    seen.set(name, idx);
    return `${name} (${idx})`;
  });
}

function formatValue(value: number, format: DisplayFormat): string {
  switch (format) {
    case "percentage":
      return `${(value * 100).toFixed(1)}%`;
    case "ratio":
      return value.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    case "absolute":
      return value.toLocaleString();
  }
}

function compactNumber(value: number): string {
  const abs = Math.abs(value);
  if (abs >= 1_000_000_000)
    return `${(value / 1_000_000_000).toLocaleString(undefined, { maximumFractionDigits: 1 })}B`;
  if (abs >= 1_000_000)
    return `${(value / 1_000_000).toLocaleString(undefined, { maximumFractionDigits: 1 })}M`;
  if (abs >= 1_000)
    return `${(value / 1_000).toLocaleString(undefined, { maximumFractionDigits: 1 })}K`;
  return value.toLocaleString(undefined, { maximumFractionDigits: 2 });
}

function formatTickValue(value: number, format: DisplayFormat): string {
  if (format === "percentage") return formatValue(value, format);
  return compactNumber(value);
}

function formatDate(dateStr: string): string {
  const d = new Date(dateStr);
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

const leftMargin = 8;

type RawValueLookup = Map<
  string,
  { numerator: number | null; denominator: number | null }
>;

function rawValueKey(seriesName: string, date: string): string {
  return `${seriesName}:${date}`;
}

function SendInsightsTooltip({
  payload,
  hoveredLine,
  seriesNames,
  displayFormat,
  numeratorLabel,
  denominatorLabel,
  rawValueLookup,
}: TooltipProps<number, string> & {
  hoveredLine: string | undefined;
  seriesNames: string[];
  displayFormat: DisplayFormat;
  numeratorLabel: string;
  denominatorLabel: string | null;
  rawValueLookup: RawValueLookup;
}) {
  if (!payload?.length || !hoveredLine) return null;

  const dateStr = payload[0]?.payload?.date as string | undefined;
  const entry = payload.find((p) => p.dataKey === hoveredLine);
  if (!entry || entry.value == null) return null;

  const color =
    SERIES_COLORS[seriesNames.indexOf(hoveredLine) % SERIES_COLORS.length]!;

  const raw =
    dateStr != null
      ? rawValueLookup.get(rawValueKey(hoveredLine, dateStr))
      : undefined;

  return (
    <ChartTooltipWrapper>
      <Row gap={2} align="stretch">
        <ChartTooltipColor color={color} />
        <Column gap={1}>
          <Text color="white" fontWeight="semibold" size="sm">
            {hoveredLine}
          </Text>
          {dateStr && (
            <Text color="whiteAlpha.80" size="sm">
              {formatDate(dateStr)}
            </Text>
          )}
          <Box
            bg="whiteAlpha.12"
            borderRadius="md"
            px={1}
            py={0.5}
            width="fit-content"
          >
            <Text
              color="white"
              fontWeight="medium"
              size="sm"
              variant="monospace"
            >
              {formatValue(entry.value as number, displayFormat)}
            </Text>
          </Box>
          {denominatorLabel && raw && (
            <Column gap={0} align="flex-start">
              {raw.numerator != null && (
                <Text color="whiteAlpha.80" size="sm">
                  {numeratorLabel}: {raw.numerator.toLocaleString()}
                </Text>
              )}
              {raw.denominator != null && (
                <Text color="whiteAlpha.80" size="sm">
                  {denominatorLabel}: {raw.denominator.toLocaleString()}
                </Text>
              )}
            </Column>
          )}
        </Column>
      </Row>
    </ChartTooltipWrapper>
  );
}

interface SendInsightsLineChartProps {
  config: ChartConfig;
  result: LineChartResult;
  resolveLabel: LabelResolver;
}

export function SendInsightsLineChart({
  config,
  result,
  resolveLabel,
}: SendInsightsLineChartProps) {
  const displayFormat = getDisplayFormat(config.denominator);
  const [hoveredLine, setHoveredLine] = useState<string | undefined>();

  const allDates = new Set<string>();
  for (const series of result.series) {
    for (const point of series.points) {
      allDates.add(point.send_date);
    }
  }
  const sortedDates = [...allDates].sort();

  const seriesNames = deduplicateNames(
    result.series.map((s) => resolveSeriesLabel(s.segments, resolveLabel)),
  );

  const rawValueLookup: RawValueLookup = new Map();
  const data = sortedDates.map((date) => {
    const row: Record<string, unknown> = { date };
    for (let i = 0; i < result.series.length; i++) {
      const series = result.series[i]!;
      const seriesName = seriesNames[i]!;
      const point = series.points.find((p) => p.send_date === date);
      row[seriesName] = point?.value ?? null;
      if (point) {
        rawValueLookup.set(rawValueKey(seriesName, date), {
          numerator: point.numerator ?? null,
          denominator: point.denominator ?? null,
        });
      }
    }
    return row;
  });

  const numeratorLabel = getNumeratorSource(config.numerator).shortLabel;
  const denominatorLabel =
    getDenominatorSource(config.denominator)?.shortLabel ?? null;

  const tickFormatter = (val: number) => formatTickValue(val, displayFormat);

  return (
    <Box height="300px" width="100%">
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart
          data={data}
          margin={{ left: leftMargin, right: -8 }}
          onMouseLeave={() => setHoveredLine(undefined)}
        >
          {renderCartesianGrid({ vertical: true })}
          <XAxis
            {...getXAxisStyles()}
            dataKey="date"
            tickFormatter={formatDate}
            minTickGap={50}
          />
          <YAxis {...getYAxisStyles()} tickFormatter={tickFormatter} />
          <RechartsTooltip
            cursor={getTooltipCursorStyles()}
            content={
              <SendInsightsTooltip
                hoveredLine={hoveredLine}
                seriesNames={seriesNames}
                displayFormat={displayFormat}
                numeratorLabel={numeratorLabel}
                denominatorLabel={denominatorLabel}
                rawValueLookup={rawValueLookup}
              />
            }
          />
          {seriesNames.length > 1 && (
            <RechartsLegend
              align="left"
              verticalAlign="top"
              wrapperStyle={{ marginLeft: `-${leftMargin}px` }}
              content={() => (
                <PillLegend
                  series={seriesNames.map((name, i) => ({
                    key: name,
                    legendName: name,
                    color: SERIES_COLORS[i % SERIES_COLORS.length]!,
                  }))}
                  onHover={(key) => setHoveredLine(key)}
                />
              )}
            />
          )}
          {seriesNames.map((name, i) => (
            <Line
              key={name}
              type="monotone"
              dataKey={name}
              stroke={SERIES_COLORS[i % SERIES_COLORS.length]}
              dot={false}
              activeDot={{
                r: 6,
                onMouseEnter: () => setHoveredLine(name),
              }}
              strokeWidth={2}
              connectNulls={false}
              onMouseEnter={() => setHoveredLine(name)}
              opacity={hoveredLine ? (hoveredLine === name ? 1 : 0.2) : 1}
            />
          ))}
        </ComposedChart>
      </ResponsiveContainer>
    </Box>
  );
}
